export class TextField {
  private readonly container: HTMLElement | null
  private readonly decreaseButton: HTMLButtonElement | null
  private readonly increaseButton: HTMLButtonElement | null
  private readonly name: string
  private isProgrammaticChange = false
  // Prevents min-clamping in onChange when focus leaves the input because a
  // counter button was pressed (mousedown moves focus before click fires).
  private counterButtonPressed = false

  constructor(private readonly element: HTMLInputElement) {
    this.element = element
    this.container = element.closest('.text-field-counter')
    this.decreaseButton = this.container?.querySelector(
      '[data-counter-decrease]'
    ) as HTMLButtonElement | null
    this.increaseButton = this.container?.querySelector(
      '[data-counter-increase]'
    ) as HTMLButtonElement | null
    this.name = element.name

    this.init()
  }

  private init(): void {
    if (!this.container || !this.decreaseButton || !this.increaseButton) return

    const setFlag = () => {
      this.counterButtonPressed = true
    }
    const clearFlag = () => {
      this.counterButtonPressed = false
    }

    this.decreaseButton.addEventListener('mousedown', setFlag)
    this.increaseButton.addEventListener('mousedown', setFlag)
    this.decreaseButton.addEventListener('mouseup', clearFlag)
    this.increaseButton.addEventListener('mouseup', clearFlag)
    this.decreaseButton.addEventListener('click', () => {
      this.decrease()
      clearFlag()
    })
    this.increaseButton.addEventListener('click', () => {
      this.increase()
      clearFlag()
    })
    this.element.addEventListener('input', () => this.onInput())
    this.element.addEventListener('change', () => this.onChange())

    this.clampInputValue({ allowEmpty: false })
    this.updateButtons()

    const form = this.element.closest('form')
    if (form) {
      form.addEventListener('reset', () => {
        setTimeout(() => this.updateButtons())
      })
    }
  }

  private onInput(): void {
    if (this.isProgrammaticChange) return

    // Only clamp to max while typing so intermediate values (e.g. "10" on the
    // way to "10000") are not immediately replaced by the min value.
    this.clampToMaxOnInput()
    this.updateButtons()
    this.emitTextFieldChange()
  }

  private onChange(): void {
    if (this.isProgrammaticChange) return

    // Only clamp to max on blur — min violations are caught by validateForm
    // when the user tries to proceed. Auto-correcting to min on blur would
    // silently replace a typed value and allow bypassing validation.
    if (!this.counterButtonPressed) {
      this.clampToMaxOnInput()
    }
    this.updateButtons()
    this.emitTextFieldChange()
  }

  private clampToMaxOnInput(): void {
    const rawValue = this.element.value.trim()
    if (rawValue === '') return

    const value = Number(rawValue)
    if (!Number.isFinite(value)) return

    const max = this.getMaxValue()
    if (value > max) {
      this.element.value = this.formatValue(max)
    }
  }

  private decrease(): void {
    this.changeValue(-this.getStep())
  }

  private increase(): void {
    this.changeValue(this.getStep())
  }

  private changeValue(delta: number): void {
    const nextValue = this.clampValue(this.getValue() + delta)

    this.element.value = this.formatValue(nextValue)
    this.updateButtons()
    this.emitChange()
  }

  private getValue(): number {
    const value = Number(this.element.value)

    if (Number.isFinite(value)) return value

    const minValue = this.getMinValue()
    return Number.isFinite(minValue) ? minValue : 0
  }

  private getStep(): number {
    const step = Number(this.element.step)

    return Number.isFinite(step) && step > 0 ? step : 1
  }

  private getMinValue(): number {
    const value = Number(
      this.element.min || this.container?.dataset.minValue || 1
    )

    return Number.isFinite(value) ? value : 1
  }

  private getMaxValue(): number {
    const value = Number(this.element.max || this.container?.dataset.maxValue)

    return Number.isFinite(value) ? value : Infinity
  }

  private clampValue(value: number): number {
    return Math.min(Math.max(value, this.getMinValue()), this.getMaxValue())
  }

  private clampInputValue({ allowEmpty }: { allowEmpty: boolean }): void {
    const rawValue = this.element.value.trim()

    if (allowEmpty && rawValue === '') return

    const value = Number(rawValue)
    const nextValue = Number.isFinite(value)
      ? this.clampValue(value)
      : this.getMinValue()

    if (this.element.value !== this.formatValue(nextValue)) {
      this.element.value = this.formatValue(nextValue)
    }
  }

  private formatValue(value: number): string {
    const step = this.getStep()
    const precision = step.toString().split('.')[1]?.length || 0

    return precision > 0 ? value.toFixed(precision) : value.toString()
  }

  private updateButtons(): void {
    if (!this.decreaseButton || !this.increaseButton) return

    const value = this.getValue()

    this.decreaseButton.disabled = value <= this.getMinValue()
    this.increaseButton.disabled = value >= this.getMaxValue()
  }

  private emitChange(): void {
    this.isProgrammaticChange = true
    this.element.dispatchEvent(new Event('input', { bubbles: true }))
    this.element.dispatchEvent(new Event('change', { bubbles: true }))
    this.isProgrammaticChange = false

    this.emitTextFieldChange()
  }

  private emitTextFieldChange(): void {
    this.container?.dispatchEvent(
      new CustomEvent('textfield:change', {
        detail: {
          name: this.name,
          value: this.getValue()
        },
        bubbles: true
      })
    )
  }
}

export class ResetableTextField {
  private readonly resetButton: HTMLButtonElement | null

  constructor(private readonly element: HTMLInputElement) {
    this.resetButton =
      (element
        .closest('[data-field-wrapper]')
        ?.querySelector(
          '[data-field-reset-button]'
        ) as HTMLButtonElement | null) || null

    this.init()
  }

  private init(): void {
    if (!this.resetButton) return

    this.resetButton.addEventListener('click', () => this.reset())
    this.element.addEventListener('input', () => this.updateButton())
    this.element.addEventListener('change', () => this.updateButton())

    const form = this.element.closest('form')
    if (form) {
      form.addEventListener('reset', () => {
        setTimeout(() => this.updateButton())
      })
    }

    this.updateButton()
  }

  private reset(): void {
    if (this.element.disabled || this.element.readOnly) return

    this.element.value = ''
    this.element.dispatchEvent(new Event('input', { bubbles: true }))
    this.element.dispatchEvent(new Event('change', { bubbles: true }))
    this.element.focus()

    this.updateButton()
  }

  private updateButton(): void {
    if (!this.resetButton) return

    const isVisible = this.element.value.length > 0

    this.resetButton.hidden = !isVisible
    this.resetButton.classList.toggle('is-visible', isVisible)
  }
}
